import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutGrid, Table, Kanban, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import QuickSave from '../components/QuickSave';
import AdGrid from '../components/AdGrid';
import AdDetailPanel from '../components/AdDetailPanel';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';

const HomePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ads, loading, fetchAds, updateAd, deleteAd, clients, tags } = useData();
  const [viewMode, setViewMode] = useState('grid');
  const [selectedAd, setSelectedAd] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Get filter params
  const clientId = searchParams.get('client');
  const tagId = searchParams.get('tag');
  const showInbox = !clientId && !tagId;

  useEffect(() => {
    const filters = {};
    if (clientId) filters.client_id = clientId;
    if (tagId) filters.tag_id = tagId;
    if (showInbox) filters.inbox = true;
    fetchAds(filters);
  }, [clientId, tagId, showInbox, fetchAds]);

  const handleAdClick = (ad) => {
    setSelectedAd(ad);
    setIsPanelOpen(true);
  };

  const handleToggleFavorite = async (ad) => {
    try {
      await updateAd(ad.id, { is_favorite: !ad.is_favorite });
      toast.success(ad.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (ad) => {
    if (window.confirm('Delete this ad?')) {
      try {
        await deleteAd(ad.id);
        toast.success('Ad deleted');
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const clearFilter = () => {
    navigate('/');
  };

  const getPageTitle = () => {
    if (clientId) {
      const client = clients.find(c => c.id === clientId);
      return client ? client.name : 'Client';
    }
    if (tagId) {
      const tag = tags.find(t => t.id === tagId);
      return tag ? `#${tag.name}` : 'Tag';
    }
    return 'Inbox';
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {getPageTitle()}
            </h1>
            {(clientId || tagId) && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-zinc-500 hover:text-white"
                onClick={clearFilter}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-zinc-800/50 rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-500'}`}
              onClick={() => setViewMode('grid')}
              data-testid="view-grid"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 ${viewMode === 'table' ? 'bg-zinc-700 text-white' : 'text-zinc-500'}`}
              onClick={() => setViewMode('table')}
              data-testid="view-table"
            >
              <Table className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 ${viewMode === 'board' ? 'bg-zinc-700 text-white' : 'text-zinc-500'}`}
              onClick={() => setViewMode('board')}
              data-testid="view-board"
            >
              <Kanban className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Quick Save */}
        <QuickSave />
      </div>

      {/* Filter Tags */}
      {showInbox && ads.length > 0 && (
        <div className="flex items-center gap-2 mb-6 text-sm">
          <span className="text-zinc-500">
            {ads.length} {ads.length === 1 ? 'item' : 'items'} in inbox
          </span>
          <Badge variant="outline" className="border-amber-500/50 text-amber-400">
            Unsorted
          </Badge>
        </div>
      )}

      {/* Ad Grid */}
      <AdGrid
        ads={ads}
        loading={loading}
        onAdClick={handleAdClick}
        onToggleFavorite={handleToggleFavorite}
        onDelete={handleDelete}
      />

      {/* Detail Panel */}
      <AdDetailPanel
        ad={selectedAd}
        open={isPanelOpen}
        onClose={() => {
          setIsPanelOpen(false);
          setSelectedAd(null);
        }}
      />
    </div>
  );
};

export default HomePage;
